import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "@tanstack/react-store";
import type { CarouselRefProps, ImageProps } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useAuth } from "../../hooks/useAuth";
import { localStorageStore, setLocalStorageByKey } from "../../stores/localStorageStore";
import { localStorageConfigStore, setLocalStorageConfigByKey } from "../../stores/localStorageConfigStore";
import { getEnvironment } from "../../utils/environment";

type CarouselItem = {
  asset: ImageProps["asset"];
  title: string;
  text: string;
};

export const useOnboardingTourController = () => {
  const { internal } = getEnvironment();
  const i18n = useI18n();
  const { isFirstLogin } = useAuth();
  const psmConfigFirstLogin = useSelector(localStorageConfigStore, ({ psmConfigFirstLogin }) => psmConfigFirstLogin);
  const psmOnboardingTourStep = useSelector(localStorageStore, ({ psmOnboardingTourStep }) => psmOnboardingTourStep);
  const carouselRefInternal = useRef<CarouselRefProps>(null);
  const [carouselReady, setCarouselReady] = useState(false);

  // Callback ref to detect when carousel is mounted
  const carouselRef = useCallback((node: CarouselRefProps | null) => {
    carouselRefInternal.current = node;
    if (node) {
      setCarouselReady(true);
    }
  }, []);

  const carouselItems: CarouselItem[] = useMemo(
    () => [
      {
        asset: {
          setName: "illustrations-partner-thumbnails",
          assetName: "Laptop",
        },
        title: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_one_header")),
        text: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_one_body")),
      },
      {
        asset: {
          setName: "illustrations-partner-thumbnails",
          assetName: "BoardChartUp",
        },
        title: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_two_header")),
        text: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_two_body")),
      },
      {
        asset: {
          setName: "illustrations-partner-thumbnails",
          assetName: "Protect",
        },
        title: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_three_header")),
        text: i18n.trans(t("flights_airline_portal_airline_view_onboarding_step_three_body")),
      },
    ],
    [i18n]
  );

  const carouselIndex = useMemo(() => {
    // check if psmOnboardingTourStep valid
    if (
      psmOnboardingTourStep !== undefined &&
      psmOnboardingTourStep >= 0 &&
      psmOnboardingTourStep < carouselItems.length
    ) {
      return psmOnboardingTourStep;
    }

    return undefined;
  }, [carouselItems, psmOnboardingTourStep]);

  const setCarouselIndex = (index: number) => {
    setLocalStorageByKey("psmOnboardingTourStep", index);
  };

  const resetConfigFirstLogin = useCallback(() => {
    if (internal && psmConfigFirstLogin) {
      setLocalStorageConfigByKey("psmConfigFirstLogin", false);
      setLocalStorageByKey("psmOnboardingTourStep", undefined);
    }
  }, [internal, psmConfigFirstLogin]);

  const onSkipTour = () => {
    // set step to -1 to indicate onboarding tour is skipped
    setLocalStorageByKey("psmOnboardingTourStep", -1);
    resetConfigFirstLogin();
  };

  const onPrevious = useCallback(() => {
    if (carouselIndex === undefined) return;

    if (carouselIndex > 0) {
      carouselRefInternal.current?.navigateBack();
    }
  }, [carouselIndex]);

  const onNext = useCallback(() => {
    if (carouselIndex === undefined) return;

    if (carouselIndex === carouselItems.length - 1) {
      // set step to -1 to indicate onboarding tour is completed
      setLocalStorageByKey("psmOnboardingTourStep", -1);
      resetConfigFirstLogin();
    } else {
      carouselRefInternal.current?.navigateForward();
    }
  }, [carouselIndex, carouselItems, resetConfigFirstLogin]);

  const active = useMemo(() => carouselIndex !== undefined && carouselIndex >= 0, [carouselIndex]);

  const isFirstStep = useMemo(() => carouselIndex !== undefined && carouselIndex === 0, [carouselIndex]);

  const isLastStep = useMemo(
    () => carouselIndex !== undefined && carouselIndex === carouselItems.length - 1,
    [carouselIndex, carouselItems]
  );

  useEffect(() => {
    if (isFirstLogin) {
      // initialize onboarding tour step
      if (psmOnboardingTourStep === undefined) {
        setLocalStorageByKey("psmOnboardingTourStep", 0);
      }
    } else {
      // remove from localStorage after first login if the tour is skipped or completed
      if (psmOnboardingTourStep !== undefined && psmOnboardingTourStep < 0) {
        setLocalStorageByKey("psmOnboardingTourStep", undefined);
      }
    }
  }, [isFirstLogin, psmOnboardingTourStep]);

  // Initialize carousel at the stored step when carousel is ready
  useEffect(() => {
    if (carouselReady && carouselIndex !== undefined && carouselIndex > 0) {
      carouselRefInternal.current?.navigate(carouselIndex, { instant: true });
    }
  }, [carouselIndex, carouselReady]);

  return {
    active,
    carouselItems,
    carouselIndex,
    carouselRef,
    setCarouselIndex,
    isFirstStep,
    isLastStep,
    onSkipTour,
    onPrevious,
    onNext,
  };
};
